import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollArea, Stack, NavLink, Box, Tooltip, Text } from '@mantine/core';
import { usePreferencesStore } from '@/stores';
import {
  IconLayoutDashboard,
  IconUserCircle,
  IconSettings,
  IconFilePlus,
  IconUsersGroup,
  IconAddressBook,
  IconCalendar,
  IconSchool,
  IconBook,
  IconClipboardList,
  IconBriefcase,
  IconDoorEnter,
  IconUserCheck,
  IconCircleCheck,
  IconHourglass,
  IconClock,
  IconUserX,
  IconUserPlus,
  IconFileExport,
} from '@tabler/icons-react';
import { AccessControl, hasAccess } from '@/components';
import { useAuth } from '@actschurch/ccms-sdk';
import { NavCountBadge, type NavCountTone } from './NavCountBadge';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { counsellingBadgeKey, coursesBadgeKey } from '@/hooks/careBadgeKeys';
import { CASE_STATUS } from '@/pages/Counselling/caseStatusIds';
import { REGISTRATION_STATUS } from '@/pages/Grow/registrationStatusIds';
import { CAMPUS_ROLES, CAMPUS_ROLE_PREFIX, campusSlug, campusName } from '@/components/common/CampusRole';

interface MainSidebarProps {
  collapsed?: boolean;
  /** Called after a navigation item is chosen (e.g. to close the mobile drawer). */
  onNavigate?: () => void;
  /** Always render the full expanded menu (used inside the mobile drawer). */
  forceExpanded?: boolean;
}

/**
 * MainSidebar — flat single-app navigation.
 *
 * Icon-only mode (Settings → Navigation & Layout → "Icon-only menu"): the
 * sidebar collapses to an icon rail and expands on hover to reveal the full
 * menu with labels (overlaying content), sized to fit the longest label.
 *
 * The skeleton's Demo group has been removed from the menu. The routes under
 * src/pages/Demo still resolve if you type the URL, they are simply not linked.
 */

const RAIL_WIDTH = 72; // icon-only collapsed width
const EXPANDED_WIDTH = 300; // hover-expanded width (fits the longest label)

// Only roles in the CAMPUS_ROLES allowlist count
const userCampusRoles = (roles: string[]) =>
  roles.filter((r) => CAMPUS_ROLES.includes(r));

const buildCampusCaseLinks = (roles: string[]): NavNode[] =>
  userCampusRoles(roles).map((role) => ({
    label: `${campusName(role)} Cases`,
    to: `/Counselling/CampusCases/${campusSlug(role)}`,
    requiredRoles: [role],
  }));

const buildCampusRegistrationLinks = (roles: string[]): NavNode[] =>
  userCampusRoles(roles).map((role) => ({
    label: `${campusName(role)} Registrations`,
    to: `/Grow/CampusRegistrations/${campusSlug(role)}`,
    requiredRoles: [role],
  }));

type NavNode = {
  label: string;
  to?: string;
  /** Exact-match active, for a parent path that is itself a page (e.g. `/LookUp`). */
  end?: boolean;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  children?: NavNode[];
};

/**
 * Menu label to badge key, plus what the number *means*.
 *
 * Keys are namespaced with the hub domain to match `useSidebarCounts`, and the status portion is built
 * from the SDK-shaped key builders rather than hardcoded, because case and registration statuses are
 * admin-editable lookup rows.
 *
 * Tones are assigned by "who is blocked": **urgent** where someone is waiting on this user to act,
 * **warning** where work is unclaimed, **active** where it is in flight and healthy, **info** for
 * neutral totals.
 */
const countByLabel: Record<string, { key: string; tone: NavCountTone }> = {
  // Counselling, admin. New Cases is no longer in the menu, so its key is unmapped: the hub still
  // sends `admin.cases.status.0`, there is simply nothing to badge.
  'Requests':           { key: `accounselling.${counsellingBadgeKey.adminRequestPending}`,                 tone: 'warning' },
  'Assigned Cases':     { key: `accounselling.${counsellingBadgeKey.adminStatus(CASE_STATUS.Assigned)}`,   tone: 'info' },
  'In Progress Cases':  { key: `accounselling.${counsellingBadgeKey.adminStatus(CASE_STATUS.InProgress)}`, tone: 'active' },

  // Counselling, leader. Pending is the one thing actually waiting on them.
  'My Cases':           { key: `accounselling.${counsellingBadgeKey.leaderTotal}`,                         tone: 'info' },
  'Pending Cases':      { key: `accounselling.${counsellingBadgeKey.leaderPending}`,                       tone: 'urgent' },

  // Courses, facilitator. Enrolment and Active Enrolments are the same active set, one grouped by
  // course and one flat, so they deliberately share a key.
  'Enrolment':          { key: `accourses.${coursesBadgeKey.facilitatorStatus(REGISTRATION_STATUS.Active)}`,  tone: 'active' },
  'Active Enrolments':  { key: `accourses.${coursesBadgeKey.facilitatorStatus(REGISTRATION_STATUS.Active)}`,  tone: 'active' },
  'Pending Enrolments': { key: `accourses.${coursesBadgeKey.facilitatorStatus(REGISTRATION_STATUS.Pending)}`, tone: 'urgent' },

  // Courses, admin. The public intake queue, same shape as Requests on the counselling side.
  'Intakes':            { key: `accourses.${coursesBadgeKey.adminIntakePending}`,                             tone: 'warning' },

  // "All Cases" and "All Registrations" are deliberately absent. A badge on a whole-table view is a
  // total, not something to act on, so it reads as noise next to the queues that do need attention.
  // The hub still sends `adminTotal` for both if they are ever wanted back.
};

const primaryLinks: NavNode[] = [
  // Admin reporting was split per section: see the Overview entries in counsellingLinks and growLinks.
  // { label: 'Page Template', to: '/template' },
  { label: 'Dashboard', to: '/Dashboard', requiredRoles: ['ActsCareFacilitator', 'ActsCareLeader'] },
];

// information groups with no nested navigation (each gets a top-level icon, shown in rail mode)
const informationLinks: NavNode[] = [
  { label: 'Care Support Leaders', to: '/Information/CareSupportLeaders', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Facilitators', to: '/Information/facilitators', requiredRoles: ['ActsCareAdmin'] },
  // { label: 'Leaders', to: '/Information/Leaders' },
];

//header counselling with Request and case links
const counsellingLinks: NavNode[] = [
  { label: 'Overview', to: '/Counselling/Overview', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Requests', to: '/Counselling/Requests', requiredRoles: ['ActsCareAdmin'] },
  { label: 'All Cases', to: '/Counselling/Cases', requiredRoles: ['ActsCareAdmin'] },
  // New Cases was dropped from the menu at the care office's request. ⚠️ The route and page are still
  // live: the Counselling Overview links to it from the New status card and two "View all"s.
  { label: 'Assigned Cases', to: '/Counselling/AssignedCases', requiredRoles: ['ActsCareAdmin'] },
  { label: 'In Progress Cases', to: '/Counselling/InProgressCases', requiredRoles: ['ActsCareAdmin'] },
  // Leader-only: both pages read the *signed-in leader's* own cases via leaderPortal, so an admin
  // has nothing to show here. The route is gated to ActsCareLeader too — listing it for admins
  // just sent them to the access-denied panel. Admin equivalents are the status pages above.
  { label: 'My Cases', to: '/Counselling/MyCases', requiredRoles: ['ActsCareLeader'] },
  { label: 'Pending Cases', to: '/Counselling/PendingCases', requiredRoles: ['ActsCareLeader'] },
];



//header Grow with Registration, Courses, Course Date
const growLinks: NavNode[] = [
  { label: 'Overview', to: '/Grow/Overview', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Intakes', to: '/Grow/Intakes', requiredRoles: ['ActsCareAdmin'] },
  { label: 'All Registrations', to: '/Grow/Registration', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Courses', to: '/Grow/Courses', requiredRoles: ['ActsCareAdmin'] },
  // { label: 'Course Dates', to: '/Grow/CourseDates' },
  // { label: 'Assigned Courses', to: '/Grow/AssignedCourses', requiredRoles: ['ActsCareFacilitator'] },
];

//header Registration with enrolment grouped by status {Pending, Active and cancelled}
const registrationLinks: NavNode[] = [
  // Not `end: true`: /Grow/Enrolment/RegistrationDetails/:id should still light this entry. The
  // sibling nav routes below it are handled by the longest-match rule in `isLinkActive`.
  { label: 'Enrolment', to: '/Grow/Enrolment', requiredRoles: ['ActsCareFacilitator'] },
  // { label: 'Pending Enrolments', to: '/Grow/Enrolment/PendingEnrolments', requiredRoles: ['ActsCareFacilitator'] },
  { label: 'Active Enrolments', to: '/Grow/Enrolment/ActiveEnrolments', requiredRoles: ['ActsCareFacilitator'] },
  // Hidden as redundant. The route and page still exist, so /Grow/Enrolment/CancelledEnrolments and
  // any saved links keep working; only the menu entry is gone.
  // { label: 'Cancelled Enrolments', to: '/Grow/Enrolment/CancelledEnrolments', requiredRoles: ['ActsCareFacilitator'] },
];

// header Reports, Member Report, Leader Report, Need Report, Case Report
const reportsLinks: NavNode[] = [
  // { label: 'Member Report', to: '/Reports/Member' },
  // { label: 'Leader Report', to: '/Reports/Leader' },
  // { label: 'Need Report', to: '/Reports/Need' },
  { label: 'Case Report', to: '/Reports/Case' },
];

// header lookup, needs
const Lookuplinks: NavNode[] = [
  { label: 'Look Up', to: '/LookUp', end: true, requiredRoles: ['ActsCareAdmin'] },
];

// Admin-only module configuration and access management. Gated both here and on the routes.
const administrationLinks: NavNode[] = [
  { label: 'Export', to: '/Administration/Export', end: true, requiredRoles: ['ActsCareAdmin'] },
  { label: 'Users', to: '/Administration/Users', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Members', to: '/Administration/Members', requiredRoles: ['ActsCareAdmin'] },
  { label: 'Module Settings', to: '/Administration/Settings', requiredRoles: ['ActsCareAdmin'] },
];

const UserLinks: NavNode[] = [
  { label: 'Profile', to: '/profile' },
  { label: 'Settings', to: '/settings' },
]

const primaryIcon: Record<string, JSX.Element> = {
  Overview: <IconLayoutDashboard size={18} stroke={1.6} />,
  'Page Template': <IconFilePlus size={18} stroke={1.6} />,
  Dashboard: <IconLayoutDashboard size={18} stroke={1.6} />,
};

const informationIcon: Record<string, JSX.Element> = {
  'Care Support Leaders': <IconUsersGroup size={18} stroke={1.6} />,
  Facilitators: <IconUsersGroup size={18} stroke={1.6} />,
  Leaders: <IconUsersGroup size={18} stroke={1.6} />,
};

const counsellingIcon: Record<string, JSX.Element> = {
  Overview: <IconLayoutDashboard size={18} stroke={1.6} />,
  Requests: <IconClipboardList size={18} stroke={1.6} />,
  'All Cases': <IconBriefcase size={18} stroke={1.6} />,
  'Campus Cases': <IconSchool size={18} stroke={1.6} />,
  'Pending Cases': <IconHourglass size={18} stroke={1.6} />,
  'In Progress Cases': <IconClock size={18} stroke={1.6} />,
  'Assigned Cases': <IconUserCheck size={18} stroke={1.6} />,
  'Resolved Cases': <IconCircleCheck size={18} stroke={1.6} />,
  'My Cases': <IconBriefcase size={18} stroke={1.6} />,
};

const growIcon: Record<string, JSX.Element> = {
  Overview: <IconLayoutDashboard size={18} stroke={1.6} />,
  'All Registrations': <IconClipboardList size={18} stroke={1.6} />,
  'Campus Registrations': <IconClipboardList size={18} stroke={1.6} />,
  Intakes: <IconDoorEnter size={18} stroke={1.6} />,
  Courses: <IconBook size={18} stroke={1.6} />,
  'Course Dates': <IconCalendar size={18} stroke={1.6} />,
  'Assigned Courses': <IconUserCheck size={18} stroke={1.6}  />,
  Enrolment: <IconUserPlus size={18} stroke={1.6} />,
};

// Keyed by **label**. These read "… Enrolments"; keying them "… Registrations" silently returned
// undefined and the three entries rendered with no icon at all.
const registrationIcon: Record<string, JSX.Element> = {
  Enrolment: <IconUserPlus size={18} stroke={1.6} />,
  'Pending Enrolments': <IconHourglass size={18} stroke={1.6} />,
  'Active Enrolments': <IconUserCheck size={18} stroke={1.6} />,
  'Cancelled Enrolments': <IconUserX size={18} stroke={1.6} />,
};

const reportsIcon: Record<string, JSX.Element> = {
  'Member Report': <IconUserCircle size={18} stroke={1.6} />,
  'Leader Report': <IconUserCircle size={18} stroke={1.6} />,
  'Need Report': <IconClipboardList size={18} stroke={1.6} />,
  'Case Report': <IconBriefcase size={18} stroke={1.6} />,
};

const LookUpIcon: Record<string, JSX.Element> = {
  'Look Up': <IconClipboardList size={18} stroke={1.6} />,
};

const AdministrationIcon: Record<string, JSX.Element> = {
  Export: <IconFileExport size={18} stroke={1.6} />,
  Users: <IconUsersGroup size={18} stroke={1.6} />,
  Members: <IconAddressBook size={18} stroke={1.6} />,
  'Module Settings': <IconSettings size={18} stroke={1.6} />,
};

const UserIcon: Record<string, JSX.Element> = {
   Profile: <IconUserCircle size={18} stroke={1.6} />,
  Settings: <IconSettings size={18} stroke={1.6} />,
};


/**
 * Every concrete nav destination, used by `isLinkActive` to apply "longest match wins".
 *
 * Without it, a parent whose siblings live underneath it stays lit for all of them:
 * `/Grow/Enrolment` prefix-matches `/Grow/Enrolment/PendingEnrolments`, so opening any one
 * enrolment page highlighted four menu entries at once.
 */
const flattenNavPaths = (nodes: NavNode[]): string[] =>
  nodes.flatMap((n) => [
    ...(n.to ? [n.to] : []),
    ...(n.children ? flattenNavPaths(n.children) : []),
  ]);

const ALL_NAV_PATHS: string[] = flattenNavPaths([
  ...primaryLinks,
  ...informationLinks,
  ...counsellingLinks,
  ...growLinks,
  ...registrationLinks,
  ...reportsLinks,
  ...Lookuplinks,
  ...administrationLinks,
  ...UserLinks,
]);

// Top-level entries shown as icons when the rail is collapsed (each has an icon).
const railItems: { label: string; to: string; end?: boolean; icon: JSX.Element }[] = [
  ...primaryLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: primaryIcon[l.label] })),
  // include information groups as top-level rail items (use file icon as fallback)
  ...informationLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: informationIcon[l.label] })),
  // include counselling links as top-level rail items
  ...counsellingLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: counsellingIcon[l.label] })),
  // include grow links as top-level rail items
  // include registration links as top-level rail items
  ...registrationLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: registrationIcon[l.label] })),
    // include reports links as top-level rail items
  ...reportsLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: reportsIcon[l.label] })),
    // include lookup links as top-level rail items
  ...Lookuplinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: LookUpIcon[l.label] })),
  ...administrationLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: AdministrationIcon[l.label] })),
  ...UserLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: UserIcon[l.label] })),
];

export default function MainSidebar({ onNavigate, forceExpanded = false }: MainSidebarProps) {
  const navigate = useNavigate();
  const { state } = useAuth();
  const { counts } = useSidebarCountsContext();
  const { pathname } = useLocation();
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const [hovered, setHovered] = useState(false);


  // STEP 2: inside MainSidebar
const userRoles = state.user?.roles;

const campusCaseLinks = useMemo(
  () => buildCampusCaseLinks(userRoles ?? []),
  [userRoles],
);
const campusRegistrationLinks = useMemo(
  () => buildCampusRegistrationLinks(userRoles ?? []),
  [userRoles],
);

const counsellingAll = useMemo(() => [...counsellingLinks, ...campusCaseLinks], [campusCaseLinks]);
const growAll = useMemo(() => [...growLinks, ...campusRegistrationLinks], [campusRegistrationLinks]);

const dynamicRailItems = useMemo(
  () => [
    ...campusCaseLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: counsellingIcon['Campus Cases'] })),
    ...campusRegistrationLinks.map((l) => ({ label: l.label, to: l.to!, end: l.end, icon: growIcon['Campus Registrations'] })),
  ],
  [campusCaseLinks, campusRegistrationLinks],
);

  // Navigate, then notify (e.g. close the mobile drawer).
  const go = (to: string) => {
    navigate(to);
    onNavigate?.();
  };

  /**
   * The badge for a nav item, or nothing.
   *
   * Two separate reasons to render nothing, and they are not the same thing: the label has no badge
   * mapped at all, or the hub did not send that key for this user, which means "not yours to see".
   * A key that *is* present and zero still counts as mapped; `NavCountBadge` then declines to draw a
   * pointless "0".
   */
  const renderCountBadge = (label: string) => {
    const mapping = countByLabel[label];
    if (!mapping) return undefined;
    const value = counts[mapping.key];
    if (value === undefined) return undefined;
    return <NavCountBadge count={value} tone={mapping.tone} />;
  };

  // Prints the keys this menu is looking for, to compare against the `snapshot` lines the hook logs.
  // If the hub is connected and sending but nothing renders, the two lists have diverged.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    try {
      if (localStorage.getItem('debugBadges') !== '1') return;
    } catch {
      return;
    }
    console.info('[sidebar-counts] keys this menu wants:', Object.fromEntries(
      Object.entries(countByLabel).map(([label, m]) => [label, m.key]),
    ));
    console.info('[sidebar-counts] keys currently held:', Object.keys(counts));
  }, [counts]);

  // Show the full menu when forced (drawer), not in icon-only mode, or hovering.
  const expanded = forceExpanded || !menuIconOnly || hovered;
  
  // Check if the user has access to a link based on roles and permissions
  const canAccessLink = (link: NavNode) => hasAccess(
    state.user?.roles ?? [],
    state.user?.rights ?? [],
    (state.user as any)?.domains || [],
    { roles: link.requiredRoles, permissions: link.requiredPermissions },
  );

  const hasVisibleLinks = (links: NavNode[]) => links.some(canAccessLink);

  /**
   * Active when the path matches, except where a **more specific** nav entry also matches.
   *
   * Prefix matching is wanted for detail routes (`/Counselling/Cases/2915` should light "All Cases"),
   * but not where sibling nav entries live underneath a parent: `/Grow/Enrolment/PendingEnrolments`
   * must light only Pending Enrolments, not Enrolment as well.
   */
  const isLinkActive = (to: string, end = false) => {
    if (end) return pathname === to;
    if (pathname !== to && !pathname.startsWith(`${to}/`)) return false;

    return !ALL_NAV_PATHS.some(
      (other) =>
        other !== to
        && other.startsWith(`${to}/`)
        && (pathname === other || pathname.startsWith(`${other}/`)),
    );
  };

  const body = (
    <ScrollArea h="100%" type="scroll">
      <Stack gap={4} p="sm" style={{ whiteSpace: 'nowrap' }}>
        {expanded ? (
          <> 
          
              {hasVisibleLinks(primaryLinks) && <Text
                size="12px"
                c="gray.6"
                fw={500}
                tt="uppercase"
                ta="left"
                opacity={0.7}
                style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}>
                  Overview
              </Text>}

            {hasVisibleLinks(primaryLinks) && primaryLinks.map((link) => (
              <AccessControl key={link.to} roles={link.requiredRoles} mode='hide'>
              <NavLink
                key={link.to}
                label={link.label}
                leftSection={primaryIcon[link.label]}
                active={link.to ? isLinkActive(link.to, link.end) : false}
                onClick={() => link.to && go(link.to)}
              />
               </AccessControl>
              ))}
           

            {/* Non-collapsible information group header + links (always visible) */}
              
            {hasVisibleLinks(informationLinks) && <Text
              size="12px"
              c="gray.6"
              fw={500}
              tt="uppercase"
              ta="left"
              opacity={0.7}
              style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
            >
              Information
            </Text>}
            {hasVisibleLinks(informationLinks) && informationLinks.map((info) => (
              <AccessControl key={info.to} roles={info.requiredRoles} mode='hide'>
              <NavLink
                key={info.to}
                label={info.label}
                leftSection={informationIcon[info.label]}
                rightSection={renderCountBadge(info.label)}
                active={info.to ? isLinkActive(info.to, info.end) : false}
                onClick={() => info.to && go(info.to)}
              />
                </AccessControl>
            ))}
             

            {/* Non-collapsible counselling group header + links (always visible) */}
              {hasVisibleLinks(counsellingAll) && <Text
                size="12px"
                c="gray.6"
                fw={500}
                tt="uppercase"
                ta="left"
                opacity={0.7}
                style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
              >
                Counselling
              </Text>}
              {hasVisibleLinks(counsellingAll) && counsellingAll.map((item) => (
                <AccessControl key={item.to} roles={item.requiredRoles} mode='hide'>
                  <NavLink
                    key={item.to}
                    label={item.label}
                    leftSection={counsellingIcon[item.label] ?? counsellingIcon['Campus Cases']}
                    rightSection={renderCountBadge(item.label)}
                    active={item.to ? isLinkActive(item.to, item.end) : false}
                    onClick={() => item.to && go(item.to)}
                  />
                </AccessControl>
              ))}
           

            {/* Non-collapsible grow group header + links (always visible) */}
            {hasVisibleLinks(growAll) && <Text
                size="12px"
                c="gray.6"
                fw={500}
                tt="uppercase"
                ta="left"
                opacity={0.7}
                style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
              >
                Grow
              </Text>}
              {hasVisibleLinks(growAll) && growAll.map((item) => (
                <AccessControl key={item.to} roles={item.requiredRoles} mode='hide'>
                  <NavLink
                    key={item.to}
                    label={item.label}
                    leftSection={growIcon[item.label] ?? growIcon['Campus Registrations']}
                    rightSection={renderCountBadge(item.label)}
                    active={item.to ? isLinkActive(item.to, item.end) : false}
                    onClick={() => item.to && go(item.to)}
                  />
                </AccessControl>
              ))}

            {/* Non-collapsible Registration group header + links (always visible) */}
            {hasVisibleLinks(registrationLinks) && <Text
              size="12px"
              c="gray.6"
              fw={500}
              tt="uppercase"
              ta="left"
              opacity={0.7}
              style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
            >
              Registrations
            </Text>}
            {hasVisibleLinks(registrationLinks) && registrationLinks.map((item) => (
              <AccessControl key={item.to} roles={item.requiredRoles} mode='hide'>
              <NavLink
                key={item.to}
                label={item.label}
                leftSection={registrationIcon[item.label]}
                rightSection={renderCountBadge(item.label)}
                active={item.to ? isLinkActive(item.to, item.end) : false}
                onClick={() => item.to && go(item.to)}
              />
               </AccessControl>
            ))}

              {/* Non-collapsible reports group header + links (always visible) */}
              {/* <Text
              size="12px"
              c="gray.6"
              fw={500}
              tt="uppercase"
              ta="left"
              opacity={0.7}
              style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
            >
              Reports
            </Text>
            {reportsLinks.map((report) => (
              <AccessControl key={report.to} roles={report.requiredRoles} mode='hide'>
                <NavLink
                  key={report.to}
                  label={report.label}
                  leftSection={reportsIcon[report.label]}
                  active={report.to ? isLinkActive(report.to, report.end) : false}
                  onClick={() => report.to && go(report.to)}
              />
              </AccessControl>
            ))} */}

              {/* Non-collapsible lookup group header + links (always visible) */}
              <AccessControl roles={['ActsCareAdmin']} mode='hide'> 
              <Text
              size="12px"
              c="gray.6"
              fw={500}
              tt="uppercase"
              ta="left"
              opacity={0.7}
              style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
            >
              Look Up
            </Text>
            {Lookuplinks.map((lookup) => (
              <NavLink
                key={lookup.to}
                label={lookup.label}
                leftSection={LookUpIcon[lookup.label]}
                active={lookup.to ? isLinkActive(lookup.to, lookup.end) : false}
                onClick={() => lookup.to && go(lookup.to)}
              />
            ))}
            </AccessControl>

            {/* Non-collapsible administration group header + links (admin only) */}
            <AccessControl roles={['ActsCareAdmin']} mode='hide'>
              <Text
                size="12px"
                c="gray.6"
                fw={500}
                tt="uppercase"
                ta="left"
                opacity={0.7}
                style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
              >
                Administration
              </Text>
              {administrationLinks.map((item) => (
                <NavLink
                  key={item.to}
                  label={item.label}
                  leftSection={AdministrationIcon[item.label]}
                  active={item.to ? isLinkActive(item.to, item.end) : false}
                  onClick={() => item.to && go(item.to)}
                />
              ))}
            </AccessControl>

             {/* Non-collapsible User group header + links (always visible) */}
              <Text
              size="12px"
              c="gray.6"
              fw={500}
              tt="uppercase"
              ta="left"
              opacity={0.7}
              style={{ width: '100%', padding: '8px 12px', marginTop: 8 }}
            >
              User
            </Text>
            {UserLinks.map((user) => (
              <NavLink
                key={user.to}
                label={user.label}
                leftSection={UserIcon[user.label]}
                active={user.to ? isLinkActive(user.to, user.end) : false}
                onClick={() => user.to && go(user.to)}
              />
            ))}
          </>
        ) : (
          // Collapsed icon rail: top-level icons only, names via tooltip.
          [...railItems, ...dynamicRailItems].map((item) => (
            <Tooltip key={item.to} label={item.label} position="right" withArrow openDelay={200}>
              <NavLink
                aria-label={item.label}
                leftSection={item.icon}
                active={isLinkActive(item.to, item.end)}
                onClick={() => go(item.to)}
                styles={{ root: { justifyContent: 'center' }, section: { marginInlineEnd: 0 } }}
              />
            </Tooltip>
          ))
        )}
      </Stack>
    </ScrollArea>
  );

  // Normal mode: a plain full-width sidebar.
  if (forceExpanded || !menuIconOnly) return body;

  // Icon-only mode: a rail that expands on hover, overlaying the content.
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: hovered ? EXPANDED_WIDTH : RAIL_WIDTH,
        background: 'var(--mantine-color-body)',
        borderInlineEnd: '1px solid var(--mantine-color-default-border)',
        boxShadow: hovered ? 'var(--mantine-shadow-lg)' : undefined,
        transition: 'width 160ms ease',
        overflow: 'hidden',
        zIndex: 250,
      }}
    >
      {body}
    </Box>
  );
}
